import { Box, ChipProps, Stack, Tooltip, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { FDRStatus, FDRStatusEntry, FieldModel } from '@cibo/core'
import { intuitiveFromNow } from '@cibo/ui'
import { ProgramIcon, ProgramProgressChip, useProgram, useWorkflow } from '../..'

interface StatusChipProps extends ChipProps {
  // @todo support other tokens!
  dollarPotential?: number
  fdrStatusEntry: FDRStatusEntry
  workflowId?: string
  programId: string
  fieldModel: FieldModel
}

export const WorkflowStatusChip = ({
  dollarPotential,
  fdrStatusEntry,
  fieldModel,
  programId,
  workflowId,
  ...chipProps
}: StatusChipProps) => {
  const { t } = useTranslation('@cibo/landmanager/FieldsTable')
  const program = useProgram({ programId: fdrStatusEntry.programId })
  const workflow = useWorkflow(workflowId)
  const windowClosed = program?.data?.enrollmentWindow.enrollmentStatus === 'closed'
  const isEngaged = fdrStatusEntry.status.includes(FDRStatus.isEngaged)

  if (windowClosed) {
    return null
  }

  return (
    <Tooltip
      placement="top-start"
      title={
        <Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {!!program?.data && <ProgramIcon size={24} program={program.data} />}
            <Typography>{program.data?.content.fields.title}</Typography>
          </Stack>
          {!!workflow.data && (
            <Box pl={4}>
              <Stack>
                <Typography variant="body2">
                  {t('workflowFieldCount', { count: workflow.data.fieldCounts.total })}
                </Typography>
                <Typography variant="caption" color="inherit">
                  {t('lastUpdated', { ago: intuitiveFromNow(workflow.data.updatedAt) })}
                </Typography>
              </Stack>
            </Box>
          )}
        </Stack>
      }
    >
      <Stack alignSelf="start">
        <Box>
          <ProgramProgressChip
            engageType={program.data?.engageType}
            progress={windowClosed ? 'window_closed' : isEngaged ? 'engaged' : 'enrolling'}
            {...chipProps}
          />
        </Box>
        {!!dollarPotential && !windowClosed && (
          <Typography variant="caption" sx={{ paddingLeft: 3.25 }}>
            {t('potential', { context: 'usd', count: dollarPotential })}
          </Typography>
        )}
      </Stack>
    </Tooltip>
  )
}
